import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { usePillars } from "@/contexts/PillarsContext";
import { UploadSimple, X, CheckCircle, Warning, Spinner, FileText, VideoCamera, Image as ImageIcon, Stack } from "@phosphor-icons/react";

const VIDEO_EXT = new Set([".mp4", ".webm", ".mov", ".m4v"]);
const DOC_EXT = new Set([".pdf", ".docx", ".doc", ".txt"]);
const IMG_EXT = new Set([".png", ".jpg", ".jpeg"]);

const STATUS = { QUEUED: "queued", UPLOADING: "uploading", DONE: "done", ERROR: "error", SKIPPED: "skipped" };

function classifyFile(file) {
  const name = file.name || "";
  const ext = "." + (name.split(".").pop() || "").toLowerCase();
  if (VIDEO_EXT.has(ext)) return { kind: "video", ext };
  if (DOC_EXT.has(ext)) return { kind: "document", ext };
  if (IMG_EXT.has(ext)) return { kind: "image", ext };
  return { kind: "unsupported", ext };
}

function iconFor(kind) {
  if (kind === "video") return <VideoCamera size={18} weight="bold" />;
  if (kind === "document") return <FileText size={18} weight="bold" />;
  if (kind === "image") return <ImageIcon size={18} weight="bold" />;
  return <Warning size={18} weight="bold" />;
}

function stripExt(name) {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(0, i) : name;
}

export default function BulkUploadModal({ onClose, onDone }) {
  const { pillars, term } = usePillars();
  const [pillar, setPillar] = useState(pillars[0]?.slug || "");
  const [duration, setDuration] = useState(5);
  const [items, setItems] = useState([]); // { file, kind, ext, status, title, error, progress }
  const [running, setRunning] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const inputRef = useRef(null);

  const handleFiles = (fileList) => {
    const next = [...items];
    for (const f of Array.from(fileList || [])) {
      const c = classifyFile(f);
      next.push({
        file: f,
        kind: c.kind,
        ext: c.ext,
        title: stripExt(f.name),
        status: c.kind === "unsupported" ? STATUS.SKIPPED : STATUS.QUEUED,
        error: c.kind === "unsupported" ? `Unsupported type ${c.ext}` : "",
        progress: 0,
      });
    }
    setItems(next);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
  };

  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const updateTitle = (idx, title) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, title } : it));

  /** Upload a single item end-to-end. Each file becomes its own lesson asset. */
  const uploadOne = async (it) => {
    const fd = new FormData();
    fd.append("title", it.title.trim() || stripExt(it.file.name));
    fd.append("pillar", pillar);
    fd.append("duration_minutes", String(duration || 5));

    if (it.kind === "video") {
      fd.append("content_type", "video");
      fd.append("file", it.file);
      await api.post("/admin/lesson-assets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } else {
      // Documents + images: create a text-stub asset, then attach the file as a document.
      fd.append("content_type", "text");
      fd.append("text_body", it.kind === "image"
        ? "Image attached — open in the lesson player."
        : "Document attached — open in the lesson player.");
      fd.append("summary", `${it.kind === "image" ? "Image" : "Document"}: ${it.file.name}`);
      const { data: created } = await api.post("/admin/lesson-assets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const docFd = new FormData();
      docFd.append("name", it.file.name);
      docFd.append("file", it.file);
      await api.post(`/admin/lesson-assets/${created.id}/document`, docFd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
  };

  const startUpload = async () => {
    if (!pillar) { return; }
    setRunning(true);
    setDoneCount(0);
    // Mark queued → uploading sequentially (avoid swamping the proxy with parallel chunks)
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.status !== STATUS.QUEUED) continue;
      setItems((arr) => arr.map((x, k) => k === i ? { ...x, status: STATUS.UPLOADING, error: "" } : x));
      try {
        await uploadOne(it);
        setItems((arr) => arr.map((x, k) => k === i ? { ...x, status: STATUS.DONE, progress: 100 } : x));
        setDoneCount((n) => n + 1);
      } catch (e) {
        const msg = formatApiError(e);
        setItems((arr) => arr.map((x, k) => k === i ? { ...x, status: STATUS.ERROR, error: msg } : x));
      }
    }
    setRunning(false);
    onDone?.();
  };

  const queuedCount = items.filter((i) => i.status === STATUS.QUEUED).length;
  const allDone = items.length > 0 && items.every((i) => i.status !== STATUS.QUEUED && i.status !== STATUS.UPLOADING);

  return (
    <motion.div
      data-testid="bulk-upload-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FFFFFF] border border-[#C4C8CE] rounded-3xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold font-display flex items-center gap-2">
            <Stack size={22} weight="bold" /> Bulk upload to Library
          </h3>
          <button data-testid="bulk-close" className="btn-ghost p-2" onClick={onClose}>
            <X size={18} weight="bold" />
          </button>
        </div>

        {pillars.length === 0 ? (
          <div className="text-[#FF5100] p-4 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE] text-sm">
            Create at least one {term.toLowerCase()} first — every uploaded asset must be assigned to one.
          </div>
        ) : (
          <>
            {/* Batch settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Assign all to {term}</div>
                <select
                  data-testid="bulk-pillar"
                  className="input-base w-full"
                  value={pillar}
                  onChange={(e) => setPillar(e.target.value)}
                  disabled={running}
                >
                  {pillars.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Default duration (min)</div>
                <input
                  data-testid="bulk-duration"
                  type="number" min={1} max={240}
                  className="input-base w-full"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value) || 5)}
                  disabled={running}
                />
              </div>
            </div>

            {/* Drop zone */}
            <div
              data-testid="bulk-dropzone"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={onDrop}
              onClick={() => !running && inputRef.current?.click()}
              className="rounded-2xl border-2 border-dashed border-[#C4C8CE] p-8 text-center mb-4 transition-all hover:border-[#CCFF00]"
              style={{ cursor: running ? "not-allowed" : "pointer" }}
            >
              <UploadSimple size={32} weight="bold" className="mx-auto mb-2 text-[#CCFF00]" />
              <div className="font-semibold mb-1">Drop files here or click to browse</div>
              <div className="text-xs text-[#000000]">
                Videos (.mp4 .webm .mov .m4v) · Docs (.pdf .docx .doc .txt) · Images (.png .jpg .jpeg)
              </div>
              <input
                ref={inputRef}
                data-testid="bulk-file-input"
                type="file"
                multiple
                hidden
                accept=".mp4,.webm,.mov,.m4v,.pdf,.docx,.doc,.txt,.png,.jpg,.jpeg"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>

            {/* File list */}
            {items.length > 0 && (
              <div className="flex flex-col gap-2 mb-4 max-h-[280px] overflow-y-auto">
                {items.map((it, idx) => (
                  <div
                    key={idx}
                    data-testid={`bulk-item-${idx}`}
                    className="flex items-center gap-2 p-2 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE]"
                  >
                    <div className="text-[#000000] w-6 flex justify-center">{iconFor(it.kind)}</div>
                    <input
                      data-testid={`bulk-item-title-${idx}`}
                      className="input-base flex-1 text-sm"
                      value={it.title}
                      onChange={(e) => updateTitle(idx, e.target.value)}
                      disabled={running || it.status === STATUS.DONE}
                      placeholder="Title"
                    />
                    <div className="text-[10px] uppercase tracking-wider w-20 text-right">
                      {it.status === STATUS.QUEUED && <span className="text-[#000000]">Queued</span>}
                      {it.status === STATUS.UPLOADING && <span className="text-[#CCFF00] flex items-center gap-1 justify-end"><Spinner size={12} className="animate-spin" /> Sending</span>}
                      {it.status === STATUS.DONE && <span className="text-[#10B981] flex items-center gap-1 justify-end"><CheckCircle size={14} weight="bold" /> Done</span>}
                      {it.status === STATUS.ERROR && <span className="text-[#FF5100] flex items-center gap-1 justify-end" title={it.error}><Warning size={14} weight="bold" /> Failed</span>}
                      {it.status === STATUS.SKIPPED && <span className="text-[#000000]" title={it.error}>Skipped</span>}
                    </div>
                    {!running && it.status !== STATUS.DONE && (
                      <button data-testid={`bulk-remove-${idx}`} className="btn-ghost p-1" onClick={() => removeItem(idx)} aria-label="Remove">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#C4C8CE]">
              <div className="text-xs text-[#000000]">
                {items.length === 0 && "Add files to begin."}
                {items.length > 0 && !running && !allDone && `${queuedCount} ready · ${doneCount} uploaded`}
                {running && `Uploading… ${doneCount}/${items.filter(i=>i.status!==STATUS.SKIPPED).length}`}
                {allDone && items.length > 0 && `${doneCount} uploaded successfully.`}
              </div>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={onClose} disabled={running}>
                  {allDone ? "Close" : "Cancel"}
                </button>
                {!allDone && (
                  <button
                    data-testid="bulk-start-upload"
                    className="btn-primary"
                    disabled={running || queuedCount === 0 || !pillar}
                    onClick={startUpload}
                  >
                    {running ? "Uploading…" : `Upload ${queuedCount} file${queuedCount === 1 ? "" : "s"}`}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
